import { useLocalStorage } from '@mantine/hooks';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { alpha, Box, ClickAwayListener, IconButton, Popover, SxProps, Typography } from '@mui/material';
import { useId, useMemo, useState } from 'react';

interface InfoButtonProps {
    ariaLabel?: string;
    maxWidth?: number;
    placement?: 'top' | 'bottom' | 'left' | 'right';
    size?: 'small' | 'medium';
    sx?: SxProps;
    title: string;
}

const placementOrigins = {
    bottom: {
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' } as const,
        transformOrigin: { horizontal: 'center', vertical: 'top' } as const,
    },
    left: {
        anchorOrigin: { horizontal: 'left', vertical: 'center' } as const,
        transformOrigin: { horizontal: 'right', vertical: 'center' } as const,
    },
    right: {
        anchorOrigin: { horizontal: 'right', vertical: 'center' } as const,
        transformOrigin: { horizontal: 'left', vertical: 'center' } as const,
    },
    top: {
        anchorOrigin: { horizontal: 'center', vertical: 'top' } as const,
        transformOrigin: { horizontal: 'center', vertical: 'bottom' } as const,
    },
};

export default function InfoButton({
    ariaLabel = 'More information',
    maxWidth = 320,
    placement = 'top',
    size = 'small',
    sx,
    title,
}: Readonly<InfoButtonProps>) {
    const [showInfoButton] = useLocalStorage({ defaultValue: true, key: 'showInfoButton' });
    const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);
    const popoverId = useId();

    const { anchorOrigin, transformOrigin } = useMemo(() => {
        return placementOrigins[placement];
    }, [placement]);

    const open = Boolean(anchorElement);

    const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorElement((currentAnchor) => (currentAnchor ? null : event.currentTarget));
    };

    const handleClose = () => {
        setAnchorElement(null);
    };

    if (!showInfoButton) {
        return null;
    }

    return (
        <>
            <IconButton
                aria-controls={open ? popoverId : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="dialog"
                aria-label={ariaLabel}
                color="primary"
                onClick={handleToggle}
                size={size}
                sx={{
                    '&:hover': {
                        backgroundColor: alpha('#1976d2', 0.12),
                        borderColor: 'primary.main',
                    },
                    backgroundColor: open ? 'action.selected' : alpha('#1976d2', 0.06),
                    border: '1px solid',
                    borderColor: open ? 'primary.main' : alpha('#1976d2', 0.2),
                    borderRadius: 999,
                    color: open ? 'primary.main' : 'mlBlue.main',
                    height: size === 'small' ? 24 : 30,
                    p: size === 'small' ? 0.375 : 0.5,
                    transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
                    width: size === 'small' ? 24 : 30,
                    ...sx,
                }}
            >
                <InfoOutlinedIcon fontSize={size} />
            </IconButton>

            <Popover
                anchorEl={anchorElement}
                anchorOrigin={anchorOrigin}
                id={popoverId}
                onClose={handleClose}
                open={open}
                slotProps={{
                    paper: {
                        sx: {
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            boxShadow: 6,
                            maxWidth,
                            mb: placement === 'top' ? 0.75 : 0,
                            ml: placement === 'right' ? 0.75 : 0,
                            mr: placement === 'left' ? 0.75 : 0,
                            mt: placement === 'bottom' ? 0.75 : 0,
                            overflow: 'visible',
                            width: 'min(calc(100vw - 32px), 100%)',
                        },
                    },
                }}
                transformOrigin={transformOrigin}
            >
                <ClickAwayListener onClickAway={handleClose}>
                    <Box sx={{ p: 1.5 }}>
                        <Typography sx={{ color: 'text.primary', lineHeight: 1.55 }} variant="body2">
                            {title}
                        </Typography>
                    </Box>
                </ClickAwayListener>
            </Popover>
        </>
    );
}
